<template>
  <div
    :class="['content-container']"
    ref="contentContainerSrc"
    :style="{
      '--font-multiplier': String(fontMultiplier),
      display: 'inline',
    }"
    :dir="isRightToLeft ? 'rtl' : 'ltr'"
  >
    <h4
      v-for="(messageDetails, index) in chatContents"
      :key="getKey(messageDetails)"
      :class="getClassTextSource(messageDetails, index)"
      :dir="isRightToLeft ? 'rtl' : 'ltr'"
      :style="{ wordBreak: isKeepAll ? 'keep-all' : 'break-all' }"
    >
      <AnimationTyping
        :is-visible="messageDetails.highlight"
        :text="messageDetails.content"
        :dir="isRightToLeft ? 'rtl' : 'ltr'"
      />
      <pre v-if="!messageDetails.highlight" class="pre-message">{{ messageDetails.content }}</pre>
    </h4>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, Ref, ref } from 'vue';

import AnimationTyping from '@/components/animations/AnimationTyping.vue';
import { useMessageTextCommon } from '@/composables/domains/chat/useMessageTextCommon';
import { useUserStore } from '@/stores/user';
import { MessageText } from '@/types/socket';
import { ij_isLastIsOwn } from '@/utils/injectionKeys.js';

const userStore = useUserStore();

const { isSourcePlaying, chatContents } = defineProps<{
  isSourcePlaying: boolean;
  chatContents: MessageText[];
  isRightToLeft: boolean;
  isKeepAll: boolean;
}>();

const isLastIsOwn = inject(ij_isLastIsOwn) as Ref<boolean>; // 이 메시지가 나의 메세지 중 마지막인지

const contentContainerSrc = ref<HTMLElement>();

// 글자 크기 배율
const fontMultiplier = computed(() => userStore.userPreferences.textSizeMultiplier);

//문장별 클래스
const getClassTextSource = (content: MessageText, index: number): object => {
  const isLastPart = index === chatContents.length - 1;
  return {
    'sound-playing': isSourcePlaying,
    'highlight-origin': isLastPart ? (!isLastIsOwn.value ? false : (content.highlight ?? false)) : false,
    'highlight-origin-fadeout': (!isLastIsOwn.value || content.fadeout) ?? false,
    'display-block': content.isThoroughlyComplete,
  };
};

const { getKey } = useMessageTextCommon({
  contentContainer: contentContainerSrc,
});
</script>

<style scoped lang="scss">
.display-block {
  display: block !important;
}

.content-container {
  display: inline-flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  min-height: 1px;
  height: auto;
  padding: 0;
  transition:
    height 0.7s ease,
    min-height 0.7s ease;
  text-align: start;
}

h4 {
  font-size: calc($font-size-h4 * var(--font-multiplier, 1));
  unicode-bidi: isolate;
}

/* 오리지널 - 통역/입력중인 텍스트 */
.highlight-origin {
  display: inline;
  color: var(--font-dim);
  animation: none;
}

/* 오리지널 - 통역 완료된 텍스트 */
.highlight-origin-fadeout {
  display: inline;
  animation: fadeIn 3s ease-in-out forwards;
}

.pre-message {
  white-space: pre-wrap;
  font-family: inherit;
  font-size: inherit;
  display: inline;
  overflow-wrap: anywhere;
  margin: 0;
  padding: 0;
}

@keyframes fadeIn {
  0% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
</style>
